import { useEffect, useState } from 'react';
import { siteAPI } from '../services/api';
import {
  normalizeBrandLogoAssets,
  resolveBrandLogoByScope,
  resolveScopedBrandLogoUrl,
  resolvePublicAssetUrl,
  resolveWorkspaceBranding,
  unwrapSiteConfigValue,
  type BrandLogoAsset,
} from '../utils/siteConfig';

const DEFAULT_SLOGAN = 'AI驱动的个体化精准给药平台';
const DEFAULT_LOGO_URL = '/images/logo.png';
const DEFAULT_SHELL_LOGO_URL = '/images/logoadmin.png';
const DEFAULT_SHELL_ICON_URL = '/images/logo-white-128.png';
const DEFAULT_SITE_NAME = 'PreciDose';
const DEFAULT_COPYRIGHT = `© ${new Date().getFullYear()} ${DEFAULT_SITE_NAME}. All rights reserved.`;

const pickConfigString = (value: unknown): string | undefined => {
  const unwrapped = unwrapSiteConfigValue<string | undefined>(value);
  return typeof unwrapped === 'string' && unwrapped.trim() ? unwrapped.trim() : undefined;
};

export const getBrandMonogram = (siteName?: string): string => {
  const normalized = (siteName || DEFAULT_SITE_NAME).trim();
  const capitals = normalized.match(/[A-Z]/g);
  if (capitals && capitals.length >= 2) {
    return capitals.slice(0, 2).join('');
  }
  const latinWords = normalized.match(/[A-Za-z0-9]+/g);
  if (latinWords && latinWords.length >= 2) {
    return `${latinWords[0][0]}${latinWords[1][0]}`.toUpperCase();
  }
  if (latinWords && latinWords[0]) {
    return latinWords[0].slice(0, 2).toUpperCase();
  }
  const compact = normalized.replace(/\s+/g, '');
  return compact.slice(0, 2).toUpperCase() || 'PD';
};

export const useSiteBranding = () => {
  const [loading, setLoading] = useState(true);
  const [slogan, setSlogan] = useState(DEFAULT_SLOGAN);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO_URL);
  const [siteName, setSiteName] = useState(DEFAULT_SITE_NAME);
  const [copyright, setCopyright] = useState(DEFAULT_COPYRIGHT);
  const [adminLogoFull, setAdminLogoFull] = useState('');
  const [adminLogoIcon, setAdminLogoIcon] = useState('');
  const [brandLogoAssets, setBrandLogoAssets] = useState<BrandLogoAsset[]>([]);

  useEffect(() => {
    Promise.allSettled([
      siteAPI.getConfig('site_name'),
      siteAPI.getConfig('site_logo'),
      siteAPI.getConfig('login_slogan'),
      siteAPI.getConfig('copyright'),
      siteAPI.getConfig('admin_logo_full'),
      siteAPI.getConfig('admin_logo_icon'),
      siteAPI.getConfig('brand_logo_assets'),
    ])
      .then((results) => {
        const [
          siteNameResult,
          siteLogoResult,
          sloganResult,
          copyrightResult,
          adminLogoFullResult,
          adminLogoIconResult,
          brandLogoAssetsResult,
        ] = results;

        if (siteNameResult.status === 'fulfilled') {
          const value = pickConfigString(siteNameResult.value?.value);
          if (value) {
            setSiteName(value);
          }
        }

        if (siteLogoResult.status === 'fulfilled') {
          const value = pickConfigString(siteLogoResult.value?.value);
          if (value) {
            setLogoUrl(value);
          }
        }

        if (sloganResult.status === 'fulfilled') {
          const value = pickConfigString(sloganResult.value?.value);
          if (value) {
            setSlogan(value);
          }
        }

        if (copyrightResult.status === 'fulfilled') {
          const value = pickConfigString(copyrightResult.value?.value);
          if (value) {
            setCopyright(value);
          }
        }

        if (adminLogoFullResult.status === 'fulfilled') {
          const value = pickConfigString(adminLogoFullResult.value?.value);
          if (value) {
            setAdminLogoFull(value);
          }
        }

        if (adminLogoIconResult.status === 'fulfilled') {
          const value = pickConfigString(adminLogoIconResult.value?.value);
          if (value) {
            setAdminLogoIcon(value);
          }
        }

        if (brandLogoAssetsResult.status === 'fulfilled') {
          setBrandLogoAssets(normalizeBrandLogoAssets(brandLogoAssetsResult.value?.value));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const resolvedLogoUrl = resolvePublicAssetUrl(logoUrl) || DEFAULT_LOGO_URL;
  const resolvedConfiguredAdminLogoFull = resolvePublicAssetUrl(adminLogoFull);
  const resolvedConfiguredAdminLogoIcon = resolvePublicAssetUrl(adminLogoIcon);
  const userWorkspaceBranding = resolveWorkspaceBranding(brandLogoAssets, {
    brandScope: 'user_workspace_brand',
    iconScope: 'user_workspace_icon',
    brandLogoUrl: adminLogoFull,
    iconLogoUrl: adminLogoIcon,
  });
  const adminWorkspaceBranding = resolveWorkspaceBranding(brandLogoAssets, {
    brandScope: 'admin_workspace_brand',
    iconScope: 'admin_workspace_icon',
    brandLogoUrl: adminLogoFull,
    iconLogoUrl: adminLogoIcon,
  });
  const resolvedAdminLogoFull = adminWorkspaceBranding.brandUrl || resolvedConfiguredAdminLogoFull;
  const resolvedAdminLogoIcon = adminWorkspaceBranding.iconUrl || resolvedConfiguredAdminLogoIcon;
  const landingHeaderLogoUrl = resolvePublicAssetUrl(
    resolveBrandLogoByScope(brandLogoAssets, 'landing_home_header', DEFAULT_LOGO_URL),
  ) || DEFAULT_LOGO_URL;
  const landingFooterLogoUrl = resolvePublicAssetUrl(
    resolveBrandLogoByScope(brandLogoAssets, 'landing_home_footer', DEFAULT_LOGO_URL),
  ) || DEFAULT_LOGO_URL;
  const userWorkspaceLogoUrl = userWorkspaceBranding.brandUrl || resolvedConfiguredAdminLogoFull;
  const userWorkspaceIconUrl = userWorkspaceBranding.iconUrl || resolvedConfiguredAdminLogoIcon;
  const userLoginLogoUrl = resolveScopedBrandLogoUrl(
    brandLogoAssets,
    'user_login',
    adminLogoFull,
    resolvedConfiguredAdminLogoFull || resolvedLogoUrl || DEFAULT_SHELL_LOGO_URL,
  );
  const adminWorkspaceLogoUrl = adminWorkspaceBranding.brandUrl || resolvedConfiguredAdminLogoFull;
  const adminWorkspaceIconUrl = adminWorkspaceBranding.iconUrl || resolvedConfiguredAdminLogoIcon;
  const adminLoginLogoUrl = resolveScopedBrandLogoUrl(
    brandLogoAssets,
    'admin_login',
    adminLogoFull,
    resolvedConfiguredAdminLogoFull || DEFAULT_SHELL_LOGO_URL,
  );
  const reportLogoUrl = resolveBrandLogoByScope(brandLogoAssets, 'report', DEFAULT_LOGO_URL);
  const resolvedReportLogoUrl = resolvePublicAssetUrl(reportLogoUrl) || DEFAULT_LOGO_URL;
  const brandMonogram = getBrandMonogram(siteName);

  return {
    loading,
    slogan,
    siteName,
    copyright,
    logoUrl,
    resolvedLogoUrl,
    adminLogoFull,
    adminLogoIcon,
    brandLogoAssets,
    resolvedAdminLogoFull,
    resolvedAdminLogoIcon,
    landingHeaderLogoUrl,
    landingFooterLogoUrl,
    userWorkspaceLogoUrl,
    userWorkspaceIconUrl,
    userLoginLogoUrl,
    adminWorkspaceLogoUrl,
    adminWorkspaceIconUrl,
    adminLoginLogoUrl,
    reportLogoUrl,
    resolvedReportLogoUrl,
    brandMonogram,
  };
};
